import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown } from 'lucide-react';
import styles from '../../_css/Pricing.module.css';
import { TIER_STYLES } from './mockData';
import { effectiveDisplayToIso, isoToEffectiveDisplay } from './pricingTransforms';

export function PageHead({ title, subtitle }) {
  return (
    <div className={styles.pageHead}>
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

export function SectionTitle({ children }) {
  return <h2 className={styles.sectionTitle}>{children}</h2>;
}

export function StatusPill({ status }) {
  const map = {
    Active: styles.pillActive,
    Live: styles.pillActive,
    Draft: styles.pillTrial,
    Scheduled: styles.pillTrial,
    Superseded: styles.pillPaused,
    Retired: styles.pillPaused,
    Paused: styles.pillPaused,
    Grant: styles.pillActive,
    'Top-up': styles.pillCustom,
    Adjust: styles.pillPaused,
    Request: styles.pillTrial,
    Edit: styles.pillActive,
    Trial: styles.pillTrial,
    Unlimited: styles.pillActive,
  };
  return <span className={`${styles.pill} ${map[status] || styles.pillCustom}`}>{status}</span>;
}

export function TierPill({ tierId }) {
  const style = TIER_STYLES[tierId] || TIER_STYLES.L;
  return (
    <span
      className={styles.pill}
      style={{
        background: style.bg,
        color: style.color,
        borderColor: style.border,
      }}
    >
      {style.label}
    </span>
  );
}

export function EuroInput({ value, onChange, width = 'w90' }) {
  const widthClass = width === 'w70' ? styles.inpW70 : width === 'w120' ? styles.inpW120 : styles.inpW90;
  return (
    <div className={`${styles.inp} ${widthClass}`}>
      <span className={styles.inpPrefix}>€</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function CreditInput({ value, onChange, suffix = 'cr', onBlur }) {
  return (
    <div className={`${styles.inp} ${styles.inpW70}`}>
      <input value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
      {suffix && <span className={styles.inpSuffix}>{suffix}</span>}
    </div>
  );
}

export function NumInput({ value, onChange }) {
  return (
    <div className={`${styles.inp} ${styles.inpW70}`}>
      <input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function DateInput({ value, onChange }) {
  const inputRef = useRef(null);
  const displayValue =
    value || isoToEffectiveDisplay(new Date().toISOString().slice(0, 10));
  const isoValue =
    effectiveDisplayToIso(displayValue) || new Date().toISOString().slice(0, 10);

  const openPicker = () => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
    } else {
      inputRef.current?.focus();
      inputRef.current?.click();
    }
  };

  return (
    <div
      className={`${styles.inp} ${styles.inpW120} ${styles.dateInp}`}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Effective from date: ${displayValue}`}
    >
      <Calendar size={14} className={styles.dateIcon} aria-hidden />
      <span className={styles.dateDisplay}>{displayValue}</span>
      <input
        ref={inputRef}
        type="date"
        className={styles.datePickerHidden}
        value={isoValue}
        onChange={(e) => onChange(isoToEffectiveDisplay(e.target.value))}
        tabIndex={-1}
        aria-hidden
      />
    </div>
  );
}

export function ToggleSwitch({ on, onToggle }) {
  return (
    <button
      type="button"
      className={`${styles.switch} ${on ? styles.switchOn : ''}`}
      onClick={onToggle}
      aria-pressed={on}
    />
  );
}

export function Panel({ children, className = '' }) {
  return <div className={`${styles.panel} ${className}`}>{children}</div>;
}

export function ActionsBar({ children }) {
  return <div className={styles.actions}>{children}</div>;
}

export function Select({ value, onChange, options, small, disabled, block }) {
  const normalized = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  return (
    <select
      className={`${styles.select} ${small ? styles.selectSm : ''} ${block ? styles.selectBlock : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {normalized.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = 'Select config',
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuStyle({
      top: rect.bottom + 6,
      left: rect.left,
      minWidth: Math.max(rect.width, 220),
    });
  };

  const handleToggle = (event) => {
    event.stopPropagation();
    if (disabled) return;

    if (open) {
      setOpen(false);
      return;
    }

    updateMenuPosition();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  const selectedLabels = options
    .filter((opt) => value.includes(opt.id))
    .map((opt) => opt.label);

  const displayLabel =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === options.length
        ? 'All sections'
        : selectedLabels.join(', ');

  const toggleOption = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((item) => item !== id));
      return;
    }
    onChange([...value, id]);
  };

  const menu =
    open &&
    menuStyle &&
    createPortal(
      <div
        ref={menuRef}
        className={styles.multiSelectMenu}
        style={{
          position: 'fixed',
          top: menuStyle.top,
          left: menuStyle.left,
          minWidth: menuStyle.minWidth,
        }}
        role="listbox"
        aria-multiselectable="true"
      >
        {options.map((opt) => {
          const checked = value.includes(opt.id);
          return (
            <label key={opt.id} className={styles.multiSelectOption}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleOption(opt.id)}
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>,
      document.body,
    );

  return (
    <div className={styles.multiSelect} ref={rootRef}>
      <button
        type="button"
        className={`${styles.multiSelectTrigger} ${open ? styles.multiSelectTriggerOpen : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabels.length ? styles.multiSelectValue : styles.multiSelectPlaceholder}>
          {displayLabel}
        </span>
        <ChevronDown size={14} className={styles.multiSelectChevron} aria-hidden />
      </button>
      {menu}
    </div>
  );
}

export function Btn({ children, primary, ghost, sm, onClick, type = 'button', disabled, title }) {
  const cls = [
    styles.btn,
    primary ? styles.btnPrimary : '',
    ghost ? styles.btnGhost : '',
    sm ? styles.btnSm : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}
